<template>
  <div>
    <!-- Header Card -->
    <b-card class="rounded-lg mb-2">
      <b-card-header>
        <div class="d-flex justify-content-between align-items-center flex-wrap">
          <div>
            <h4 class="mb-0">
              {{ $t('accounting_accounts_view') }}
            </h4>
            <p
              v-if="item"
              class="text-muted mb-0"
            >
              {{ item.code }} - {{ item.name }}
            </p>
          </div>
          <div class="d-flex align-items-center flex-wrap mt-1 mt-sm-0">
            <b-button
              v-if="hasPermission('edit accounts')"
              variant="primary"
              class="mr-1"
              :to="{ name: 'accounting-accounts-edit', params: { id: accountId } }"
            >
              {{ $t('accounting_accounts_edit') }}
            </b-button>
            <b-button
              variant="info"
              :to="{ name: 'accounting-accounts-statement', params: { id: accountId } }"
            >
              {{ $t('accounting_accounts_statement') }}
            </b-button>
          </div>
        </div>
      </b-card-header>
    </b-card>

    <!-- Loading State -->
    <div
      v-if="loadItem"
      class="text-center py-5"
    >
      <b-spinner
        variant="primary"
        label="Spinning"
      />
      <p class="mt-2">
        {{ $t('accounting_accounts_loading') }}
      </p>
    </div>

    <!-- Account Details -->
    <div v-else-if="item">
      <!-- Summary Cards -->
      <b-row class="match-height mb-2">
        <b-col
          cols="12"
          md="6"
          lg="3"
        >
          <b-card class="text-center">
            <div class="d-flex justify-content-between align-items-center mb-1">
              <h6 class="mb-0 text-muted">
                {{ $t('accounting_accounts_current_balance') }}
              </h6>
              <feather-icon
                icon="WalletIcon"
                size="20"
                class="text-primary"
              />
            </div>
            <h3 class="font-weight-bolder mb-0">
              {{ formatCurrency(item.current_balance, item.currency) }}
            </h3>
          </b-card>
        </b-col>
        <b-col
          cols="12"
          md="6"
          lg="3"
        >
          <b-card class="text-center">
            <div class="d-flex justify-content-between align-items-center mb-1">
              <h6 class="mb-0 text-muted">
                {{ $t('accounting_accounts_type') }}
              </h6>
              <feather-icon
                icon="CreditCardIcon"
                size="20"
                class="text-info"
              />
            </div>
            <h5 class="font-weight-bolder mb-0">
              <b-badge :variant="getTypeVariant(item.type && item.type.value)">
                {{ $i18n.locale === 'ar' ? (item.type && item.type.label_ar) || '' : (item.type && item.type.label) || '' }}
              </b-badge>
            </h5>
          </b-card>
        </b-col>
        <b-col
          cols="12"
          md="6"
          lg="3"
        >
          <b-card class="text-center">
            <div class="d-flex justify-content-between align-items-center mb-1">
              <h6 class="mb-0 text-muted">
                {{ $t('accounting_accounts_status') }}
              </h6>
              <feather-icon
                icon="CheckCircleIcon"
                size="20"
                class="text-success"
              />
            </div>
            <h5 class="font-weight-bolder mb-0">
              <b-badge :variant="getStatusVariant(item.status && item.status.value)">
                {{ $i18n.locale === 'ar' ? (item.status && item.status.label_ar) || '' : (item.status && item.status.label) || '' }}
              </b-badge>
            </h5>
          </b-card>
        </b-col>
        <b-col
          cols="12"
          md="6"
          lg="3"
        >
          <b-card class="text-center">
            <div class="d-flex justify-content-between align-items-center mb-1">
              <h6 class="mb-0 text-muted">
                {{ $t('accounting_accounts_currency') }}
              </h6>
              <feather-icon
                icon="DollarSignIcon"
                size="20"
                class="text-warning"
              />
            </div>
            <h3 class="font-weight-bolder mb-0">
              {{ item.currency }}
            </h3>
          </b-card>
        </b-col>
      </b-row>

      <!-- Account Information -->
      <b-card>
        <b-card-header>
          <h5 class="mb-0">
            {{ $t('accounting_accounts_account_information') }}
          </h5>
        </b-card-header>
        <b-card-body>
          <b-row>
            <b-col
              cols="12"
              md="6"
            >
              <b-row class="mb-2">
                <b-col cols="6">
                  <strong>{{ $t('accounting_accounts_code') }}:</strong>
                </b-col>
                <b-col cols="6">
                  {{ item.code || '-' }}
                </b-col>
              </b-row>
              <b-row class="mb-2">
                <b-col cols="6">
                  <strong>{{ $t('accounting_accounts_name') }}:</strong>
                </b-col>
                <b-col cols="6">
                  {{ item.name }}
                </b-col>
              </b-row>
              <b-row class="mb-2">
                <b-col cols="6">
                  <strong>{{ $t('accounting_accounts_type') }}:</strong>
                </b-col>
                <b-col cols="6">
                  <b-badge :variant="getTypeVariant(item.type && item.type.value)">
                    {{ $i18n.locale === 'ar' ? (item.type && item.type.label_ar) || '' : (item.type && item.type.label) || '' }}
                  </b-badge>
                </b-col>
              </b-row>
            </b-col>
            <b-col
              cols="12"
              md="6"
            >
              <b-row class="mb-2">
                <b-col cols="6">
                  <strong>{{ $t('accounting_accounts_status') }}:</strong>
                </b-col>
                <b-col cols="6">
                  <b-badge :variant="getStatusVariant(item.status && item.status.value)">
                    {{ $i18n.locale === 'ar' ? (item.status && item.status.label_ar) || '' : (item.status && item.status.label) || '' }}
                  </b-badge>
                </b-col>
              </b-row>
              <b-row class="mb-2">
                <b-col cols="6">
                  <strong>{{ $t('accounting_accounts_currency') }}:</strong>
                </b-col>
                <b-col cols="6">
                  {{ item.currency }}
                </b-col>
              </b-row>
              <b-row class="mb-2">
                <b-col cols="6">
                  <strong>{{ $t('accounting_accounts_current_balance') }}:</strong>
                </b-col>
                <b-col cols="6">
                  {{ formatCurrency(item.current_balance, item.currency) }}
                </b-col>
              </b-row>
              <b-row
                v-if="item.description"
                class="mb-2"
              >
                <b-col cols="6">
                  <strong>{{ $t('accounting_accounts_description') }}:</strong>
                </b-col>
                <b-col cols="6">
                  {{ item.description }}
                </b-col>
              </b-row>
              <b-row
                v-if="item.created_by_user"
                class="mb-2"
              >
                <b-col cols="6">
                  <strong>{{ $t('accounting_accounts_created_by') }}:</strong>
                </b-col>
                <b-col cols="6">
                  {{ item.created_by_user.name }}
                </b-col>
              </b-row>
              <b-row
                v-if="item.created_at"
                class="mb-2"
              >
                <b-col cols="6">
                  <strong>{{ $t('accounting_accounts_created_at') }}:</strong>
                </b-col>
                <b-col cols="6">
                  {{ formatDate(item.created_at) }}
                </b-col>
              </b-row>
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>

      <!-- Recent Entries -->
      <b-card v-if="item.entries && item.entries.length > 0">
        <b-card-header>
          <h5 class="mb-0">
            {{ $t('accounting_accounts_recent_entries') }}
          </h5>
        </b-card-header>
        <b-card-body>
          <b-table
            striped
            hover
            :items="item.entries"
            :fields="entryFields"
            responsive
          >
            <template #cell(entry_type)="data">
              <b-badge :variant="data.item.entry_type === 'credit' ? 'success' : 'danger'">
                {{ data.item.entry_type === 'credit' ? $t('accounting_accounts_credit') : $t('accounting_accounts_debit') }}
              </b-badge>
            </template>
            <template #cell(amount)="data">
              {{ formatCurrency(data.item.amount, item.currency) }}
            </template>
            <template #cell(created_at)="data">
              {{ formatDate(data.item.created_at) }}
            </template>
          </b-table>
        </b-card-body>
      </b-card>

      <!-- Activity Logs Section -->
      <b-row
        v-if="hasPermission('view activity_logs')"
        class="mt-3"
      >
        <b-col cols="12">
          <b-card header="سجل النشاطات">
            <ActivityLogs
              :model-type="'account'"
              :model-id="Number(accountId)"
              :show-filters="true"
              :per-page="15"
            />
          </b-card>
        </b-col>
      </b-row>
    </div>

    <!-- Error State -->
    <b-card
      v-else-if="!loadItem && !item"
      class="text-center"
    >
      <b-card-body>
        <feather-icon
          icon="AlertCircleIcon"
          size="48"
          class="text-danger mb-2"
        />
        <h5>{{ $t('accounting_accounts_error_loading') }}</h5>
        <b-button
          variant="primary"
          @click="loadAccountData"
        >
          {{ $t('accounting_accounts_retry') }}
        </b-button>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BButton,
  BRow,
  BCol,
  BBadge,
  BSpinner,
  BTable,
} from 'bootstrap-vue'
import { mapGetters } from 'vuex'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
import ActivityLogs from '@/components/ActivityLogs.vue'

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BButton,
    BRow,
    BCol,
    BBadge,
    BSpinner,
    BTable,
    FeatherIcon,
    ActivityLogs,
  },
  computed: {
    ...mapGetters({
      item: 'accounting/accounts/item',
      loadItem: 'accounting/accounts/loadItem',
    }),
    accountId() {
      return this.$route.params.id
    },
    entryFields() {
      return [
        { key: 'entry_type', label: this.$t('accounting_accounts_entry_type') },
        { key: 'amount', label: this.$t('accounting_accounts_amount') },
        { key: 'description', label: this.$t('accounting_accounts_description') },
        { key: 'created_at', label: this.$t('accounting_accounts_date') },
      ]
    },
  },
  async mounted() {
    await this.loadAccountData()
  },
  methods: {
    formatCurrency(amount, currency = 'EGP') {
      if (amount === null || amount === undefined) return '-'
      return new Intl.NumberFormat('ar-EG', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount)
    },
    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return new Intl.DateTimeFormat(this.$i18n.locale === 'ar' ? 'ar-EG' : 'en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date)
    },
    getTypeVariant(type) {
      const variants = {
        bank: 'primary',
        cash: 'success',
        petty_cash: 'info',
      }
      return variants[type] || 'secondary'
    },
    getStatusVariant(status) {
      const variants = {
        active: 'success',
        inactive: 'secondary',
        closed: 'dark',
      }
      return variants[status] || 'secondary'
    },
    async loadAccountData() {
      await this.$store.dispatch('accounting/accounts/get', this.accountId)
    },
  },
}
</script>

<style lang="scss">
</style>
